import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const OWNER_IMG =
  "https://customer-assets.emergentagent.com/job_salon-simple-1/artifacts/u3zr4nyd_WhatsApp%20Image%202026-04-18%20at%202.35.28%20PM.jpeg";

export default function Owner() {
  return (
    <div data-testid="owner-page" className="bg-[#000000] text-white">
      <div className="pt-24 md:pt-28 grid grid-cols-1 md:grid-cols-12 min-h-screen">
        {/* Sticky portrait */}
        <div
          data-testid="owner-portrait"
          className="md:col-span-5 md:sticky md:top-24 md:self-start p-6 md:p-10"
        >
          <div className="na-img-reveal owner-portrait-frame h-[55vh] md:h-[80vh] w-full border border-white/15 bg-[#0a0a0a] flex items-center justify-center">
            <img
              src={OWNER_IMG}
              alt="Mr. Suresh Bhandari, Founder of Style-U & 7 Hills Unisex Salon"
              className="w-full h-full object-contain owner-portrait-img"
            />
          </div>

          {/* Experience badge */}
          <div
            data-testid="owner-experience-badge"
            className="mt-6 flex items-center justify-center"
          >
            <span className="inline-flex items-baseline gap-3 border border-[#ffd36e]/60 bg-[#ffd36e]/10 px-5 py-3 rounded-full">
              <span className="font-serif-display text-2xl md:text-3xl text-[#ffd36e] leading-none">
                32+
              </span>
              <span className="label-eyebrow text-[#ffd36e]">
                Years of Experience
              </span>
            </span>
          </div>
        </div>

        {/* Scrolling content */}
        <div className="md:col-span-7 px-6 lg:px-16 py-20 md:py-32">
          <p className="label-eyebrow" data-testid="owner-eyebrow">
            The Founder
          </p>
          <h1
            data-testid="owner-name"
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mt-8"
          >
            Mr. Suresh
            <br />
            <span className="italic text-[#cfa86e]">Bhandari</span>
          </h1>
          <p className="mt-6 text-white/60 text-sm tracking-[0.25em] uppercase">
            Founder · Master Stylist · Style-U & 7 Hills
          </p>

          <div className="mt-14 space-y-8 text-white/75 leading-relaxed max-w-xl">
            <p>
              With <span className="text-[#ffd36e] font-medium">over 32 years
              of experience</span> behind the chair, Suresh Bhandari has spent
              the better part of his life shaping the craft. He began as a
              young apprentice in his family's shop, learning the steady
              rhythms of the trade — the scissors held just so, the razor
              stropped before every shave, the slow wash that is, really, the
              start of any good haircut.
            </p>
            <p>
              Today, he runs three salons — Style-U (Veena Nagar branch),
              Style-U (Tambe Nagar branch), and 7 Hills Salon in Goregaon
              West — each carrying the same quiet standard. Known as much for
              his craftsmanship as for his calm, his clients come back not
              only for the work, but for the welcome.
            </p>
            <p>
              He is a believer in simple things done well: clean tools, a
              thoughtful consultation, a cup of chai at the end of a long
              sitting, and the kind of patience that turns a haircut into a
              small, good hour of the week.
            </p>
          </div>

          {/* Philosophy block */}
          <div
            data-testid="owner-philosophy"
            className="mt-20 border-t border-white/10 pt-14"
          >
            <p className="label-eyebrow">Philosophy</p>
            <p className="font-serif-display text-3xl md:text-4xl mt-6 leading-[1.15] text-white/90 max-w-xl">
              "A good haircut is not hurried. You sit,
              <span className="italic text-[#cfa86e]"> we listen</span>, and
              then we work."
            </p>
            <p className="mt-8 text-sm tracking-[0.2em] uppercase text-white/50">
              — Mr. Suresh Bhandari
            </p>
          </div>

          {/* Timeline */}
          <div
            data-testid="owner-timeline"
            className="mt-20 border-t border-white/10 pt-14"
          >
            <p className="label-eyebrow mb-10">A Brief Chronology</p>
            <ol className="space-y-10">
              {[
                {
                  y: "1994",
                  t: "An apprenticeship",
                  d: "Learning the scissors, the razor, the quiet discipline of a family trade.",
                },
                {
                  y: "2007",
                  t: "Style-U, Tambe Nagar",
                  d: "The first salon of his own — classic grooming, modern styling, unisex from day one.",
                },
                {
                  y: "2010",
                  t: "Style-U, Veena Nagar",
                  d: "A second chair, same standards — just down the road.",
                },
                {
                  y: "2022",
                  t: "7 Hills Salon, Goregaon West",
                  d: "The third branch — a slightly larger welcome, the same quiet craft.",
                },
                {
                  y: "Today",
                  t: "Three salons, one eye",
                  d: "Each visit, at every branch, overseen personally by Mr. Bhandari.",
                },
              ].map((item, i) => (
                <li
                  key={item.y}
                  className="grid grid-cols-12 gap-6"
                  data-testid={`owner-timeline-${i}`}
                >
                  <span className="font-serif-display italic text-[#cfa86e] col-span-3 md:col-span-2">
                    {item.y}
                  </span>
                  <div className="col-span-9 md:col-span-10">
                    <h4 className="font-serif-display text-xl md:text-2xl">
                      {item.t}
                    </h4>
                    <p className="text-white/60 text-sm mt-2 leading-relaxed">
                      {item.d}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* CTA */}
          <div className="mt-20 border-t border-white/10 pt-14">
            <Link
              to="/services"
              data-testid="owner-cta-services"
              className="group inline-flex items-center gap-3 border border-white/20 hover:border-[#cfa86e] px-8 py-4 text-sm tracking-[0.25em] uppercase transition-colors duration-500"
            >
              <span className="text-white group-hover:text-[#cfa86e] transition-colors duration-500">
                Step inside the salons
              </span>
              <ArrowUpRight
                size={16}
                strokeWidth={1.25}
                className="text-[#cfa86e] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
